/**
 * OpenStreetMap Modal
 *
 * Enterprise-grade map selection for floor plan alternatives.
 * Features:
 * - 16:9 landscape selection area (fixed aspect ratio)
 * - Address search via Nominatim (free OSM geocoding)
 * - Automatic capture of both detail and overview images
 * - Visual selection overlay
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { MapContainer, TileLayer, useMap } from 'react-leaflet';
import { X, Search, Check, Loader2, Maximize2 } from 'lucide-react';
import L from 'leaflet';
import html2canvas from 'html2canvas';
import 'leaflet/dist/leaflet.css';
import { MapCaptureResult } from '@/services/mapCaptureService';
import { stitchOsmTiles } from '@/services/osmTileStitcher';
import { logger } from '@/utils/logger';

// Fix Leaflet default marker icons
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

// Leaflet's prototype carries a private `_getIconUrl` that resolves icon paths
// relative to the (bundled, hashed) CSS — wrong under Vite. Delete it so the
// explicitly-provided URLs below win. Typed shim avoids `@ts-ignore`.
delete (L.Icon.Default.prototype as unknown as { _getIconUrl?: unknown })._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

interface OpenStreetMapModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (result: MapCaptureResult) => void;
}

/** Geographic coordinate used for search results and map centering. */
interface LatLng {
  lat: number;
  lng: number;
}

/** Pixel rect of the 16:9 capture window inside the map container. */
interface SelectionBox {
  width: number;
  height: number;
  top: number;
  left: number;
}

/** Minimal shape of a Nominatim search hit (only the fields we read). */
interface NominatimResult {
  lat?: string;
  lon?: string;
}

// Selection box dimensions (16:9 aspect ratio)
const SELECTION_ASPECT_RATIO = 16 / 9;
const SELECTION_WIDTH_PERCENT = 85; // % of container width

// Default map settings
const DEFAULT_CENTER: [number, number] = [48.8566, 2.3522]; // Paris
const DEFAULT_ZOOM = 17;

// High-resolution capture tuning (native OSM tile stitch).
const MAX_OSM_ZOOM = 19;      // OSM raster tiles stop adding real detail past ~19
const HIRES_ZOOM_BOOST = 2;   // Render 2 zoom levels deeper than displayed → ~4× real detail
const MAX_STITCH_DIM = 5000;  // Px cap per axis so the canvas never OOMs a low-end phone

/**
 * Map controller component for accessing map instance
 */
const MapController: React.FC<{
  searchResult: LatLng | null;
  mapRef: React.MutableRefObject<L.Map | null>;
}> = ({ searchResult, mapRef }) => {
  const map = useMap();

  useEffect(() => {
    mapRef.current = map;
    return () => { mapRef.current = null; };
  }, [map, mapRef]);

  useEffect(() => {
    if (searchResult) {
      map.flyTo([searchResult.lat, searchResult.lng], 17, { duration: 1.5 });
    }
  }, [searchResult, map]);

  return null;
};

const OpenStreetMapModal: React.FC<OpenStreetMapModalProps> = ({ isOpen, onClose, onConfirm }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [searchResult, setSearchResult] = useState<LatLng | null>(null);
  const [isCapturing, setIsCapturing] = useState(false);
  const [initialCenter, setInitialCenter] = useState<[number, number]>(DEFAULT_CENTER);

  // Selection box dimensions
  const [selectionBox, setSelectionBox] = useState<SelectionBox | null>(null);

  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);

  // Calculate selection box dimensions
  useEffect(() => {
    if (!isOpen) return;

    const updateSelectionBox = () => {
      const container = mapContainerRef.current;
      if (!container) return;

      const rect = container.getBoundingClientRect();
      const containerWidth = rect.width;
      const containerHeight = rect.height;

      // Calculate 16:9 selection box that fits within container
      let boxWidth = containerWidth * (SELECTION_WIDTH_PERCENT / 100);
      let boxHeight = boxWidth / SELECTION_ASPECT_RATIO;

      // If too tall, constrain by height
      if (boxHeight > containerHeight * 0.7) {
        boxHeight = containerHeight * 0.7;
        boxWidth = boxHeight * SELECTION_ASPECT_RATIO;
      }

      // Center the box
      const left = (containerWidth - boxWidth) / 2;
      const top = (containerHeight - boxHeight) / 2;

      setSelectionBox({ width: boxWidth, height: boxHeight, top, left });
    };

    updateSelectionBox();
    window.addEventListener('resize', updateSelectionBox);

    return () => window.removeEventListener('resize', updateSelectionBox);
  }, [isOpen]);

  // Try to get user's location on mount
  useEffect(() => {
    if (isOpen && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setInitialCenter([position.coords.latitude, position.coords.longitude]);
          logger.info('Got user location', {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
        },
        (error) => {
          logger.warn('Could not get user location', { error: error.message });
        },
        { timeout: 5000, enableHighAccuracy: false }
      );
    }
  }, [isOpen]);

  /**
   * Search for address using Nominatim
   */
  const handleSearch = useCallback(async () => {
    if (!searchQuery.trim()) return;

    setIsSearching(true);
    setSearchError(null);

    try {
      const encoded = encodeURIComponent(searchQuery.trim());
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encoded}&limit=1`,
        {
          headers: {
            'Accept': 'application/json',
            'User-Agent': 'PinCam-FloorPlanSelector/1.0'
          }
        }
      );

      if (!response.ok) throw new Error('Search failed');

      const results: unknown = await response.json();

      if (!Array.isArray(results) || results.length === 0) {
        setSearchError('No results found. Try a different address.');
        return;
      }

      const result = results[0] as NominatimResult;
      const lat = parseFloat(result?.lat ?? '');
      const lng = parseFloat(result?.lon ?? '');

      if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        setSearchError('No results found. Try a different address.');
        return;
      }

      logger.info('Address found', { query: searchQuery, lat, lng });
      setSearchResult({ lat, lng });

    } catch (error) {
      logger.error('Address search failed', { error });
      setSearchError('Search failed. Please try again.');
    } finally {
      setIsSearching(false);
    }
  }, [searchQuery]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSearch();
  };

  /**
   * Capture the map within the 16:9 selection box.
   *
   * Preferred path: stitch native OSM tiles for the selected geographic window
   * at a HIGHER zoom than displayed → genuine high resolution, bounded by the
   * tile data rather than the small device viewport. Falls back to an
   * html2canvas snapshot only if tile fetching fails (e.g. offline).
   */
  const captureSelectionArea = useCallback(async (): Promise<{ blob: Blob; dataUrl: string }> => {
    const container = mapContainerRef.current;
    const leafletContainer = container?.querySelector('.leaflet-container') as HTMLElement | null;
    const map = mapRef.current;

    if (!container || !leafletContainer || !map || !selectionBox) {
      throw new Error('Map container not ready');
    }

    // ---- Preferred: genuine high-resolution native-tile stitch ----------
    try {
      const displayedZoom = map.getZoom();
      let renderZoom = Math.min(MAX_OSM_ZOOM, Math.round(displayedZoom + HIRES_ZOOM_BOOST));

      // Geographic corners of the on-screen 16:9 selection box.
      const nw = map.containerPointToLatLng(L.point(selectionBox.left, selectionBox.top));
      const se = map.containerPointToLatLng(
        L.point(selectionBox.left + selectionBox.width, selectionBox.top + selectionBox.height),
      );

      // World-pixel rect at the (higher) render zoom. Step the zoom down if the
      // resulting canvas would exceed the safety cap (each level halves dims).
      let nwPx = map.project(nw, renderZoom);
      let sePx = map.project(se, renderZoom);
      while (
        renderZoom > 1 &&
        Math.max(Math.abs(sePx.x - nwPx.x), Math.abs(sePx.y - nwPx.y)) > MAX_STITCH_DIM
      ) {
        renderZoom -= 1;
        nwPx = map.project(nw, renderZoom);
        sePx = map.project(se, renderZoom);
      }

      const stitched = await stitchOsmTiles({
        zoom: renderZoom,
        left: Math.min(nwPx.x, sePx.x),
        top: Math.min(nwPx.y, sePx.y),
        width: Math.abs(sePx.x - nwPx.x),
        height: Math.abs(sePx.y - nwPx.y),
        quality: 0.95,
      });
      logger.info('High-res map stitched from native tiles', {
        displayedZoom,
        renderZoom,
        width: stitched.width,
        height: stitched.height,
      });
      return { blob: stitched.blob, dataUrl: stitched.dataUrl };
    } catch (error) {
      logger.warn('Tile stitch failed; falling back to on-screen snapshot', { error });
    }

    // ---- Fallback: html2canvas snapshot of the on-screen map ------------
    // Hide controls for capture
    const controls = leafletContainer.querySelectorAll('.leaflet-control-container');
    controls.forEach(ctrl => {
      (ctrl as HTMLElement).style.visibility = 'hidden';
    });

    try {
      // The on-screen container caps real resolution, so capture at a high
      // scale (floor 4, cap 6 to avoid OOM) to salvage as much detail as
      // possible. This path only runs when the high-res stitch is unavailable.
      const captureScale = Math.min(6, Math.max(4, (window.devicePixelRatio || 1) * 2));
      // Capture the entire map
      const fullCanvas = await html2canvas(leafletContainer, {
        useCORS: true,
        allowTaint: false,
        backgroundColor: '#f0f0f0',
        logging: false,
        scale: captureScale
      });

      // Calculate crop region (selection box coordinates relative to the captured canvas)
      const containerRect = container.getBoundingClientRect();
      if (!containerRect.width || !containerRect.height) {
        throw new Error('Map container has no size');
      }
      const scaleX = fullCanvas.width / containerRect.width;
      const scaleY = fullCanvas.height / containerRect.height;

      const cropX = selectionBox.left * scaleX;
      const cropY = selectionBox.top * scaleY;
      const cropWidth = selectionBox.width * scaleX;
      const cropHeight = selectionBox.height * scaleY;

      // Create cropped canvas (16:9)
      const croppedCanvas = document.createElement('canvas');
      croppedCanvas.width = cropWidth;
      croppedCanvas.height = cropHeight;

      const ctx = croppedCanvas.getContext('2d');
      if (!ctx) throw new Error('Failed to get canvas context');

      ctx.drawImage(
        fullCanvas,
        cropX, cropY, cropWidth, cropHeight,
        0, 0, cropWidth, cropHeight
      );

      // Convert to blob
      return new Promise<{ blob: Blob; dataUrl: string }>((resolve, reject) => {
        croppedCanvas.toBlob(
          (blob) => {
            if (blob) {
              const reader = new FileReader();
              reader.onload = () => resolve({ blob, dataUrl: reader.result as string });
              reader.onerror = () => reject(new Error('Failed to read blob'));
              reader.readAsDataURL(blob);
            } else {
              reject(new Error('Failed to create blob'));
            }
          },
          'image/jpeg',
          0.98 // High quality so fine map line-art isn't lost to compression
        );
      });
    } finally {
      // Restore controls
      controls.forEach(ctrl => {
        (ctrl as HTMLElement).style.visibility = 'visible';
      });
    }
  }, [selectionBox]);

  /**
   * Capture both detail and overview views
   */
  const handleConfirmSelection = useCallback(async () => {
    const map = mapRef.current;

    if (!map || !selectionBox) {
      logger.error('Map not ready for capture');
      setSearchError('Map not ready. Please try again.');
      return;
    }

    setIsCapturing(true);
    setSearchError(null);

    try {
      const center = map.getCenter();
      const detailZoom = map.getZoom();
      // Zoom out 5 levels for a clear distinction between detail and overview
      // This creates a noticeable difference: detail shows street-level, overview shows neighborhood
      const overviewZoom = Math.max(1, detailZoom - 5);

      // Wait for tiles to load (detectRetina fetches ~4× as many)
      await new Promise(resolve => setTimeout(resolve, 2500));

      // Capture detail view (current zoom - this is what user sees)
      logger.debug('Capturing detail view at zoom', { zoom: detailZoom });
      const detail = await captureSelectionArea();

      // Zoom out significantly for overview (neighborhood context)
      logger.debug('Zooming out for overview', { from: detailZoom, to: overviewZoom });
      map.setZoom(overviewZoom, { animate: false });

      // Wait longer for tiles to load at lower zoom (more tiles needed)
      await new Promise(resolve => setTimeout(resolve, 3500));

      // Capture overview view (zoomed out - shows larger area)
      logger.debug('Capturing overview view at zoom', { zoom: overviewZoom });
      const overview = await captureSelectionArea();

      // Restore original zoom
      map.setZoom(detailZoom, { animate: false });

      const result: MapCaptureResult = {
        detailBlob: detail.blob,
        overviewBlob: overview.blob,
        detailDataUrl: detail.dataUrl,
        overviewDataUrl: overview.dataUrl,
        center: { lat: center.lat, lng: center.lng },
        detailZoom,
        overviewZoom
      };

      logger.info('Map capture successful', {
        center: result.center,
        detailZoom: result.detailZoom,
        overviewZoom: result.overviewZoom
      });

      onConfirm(result);
      onClose();
    } catch (error) {
      logger.error('Map capture failed', { error });
      setSearchError('Failed to capture map. Please try again.');
    } finally {
      setIsCapturing(false);
    }
  }, [selectionBox, captureSelectionArea, onConfirm, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[400] bg-black flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-3 pt-[max(12px,env(safe-area-inset-top))] bg-blue-600/80 border-b border-blue-400">
        <h2 className="text-lg font-bold text-white">Select Map Area</h2>
        <button
          onClick={onClose}
          className="p-2 text-white hover:text-yellow-400 transition-colors"
          disabled={isCapturing}
        >
          <X size={28} />
        </button>
      </div>

      {/* Search Bar */}
      <form onSubmit={handleSearchSubmit} className="p-3 bg-black/70 border-b border-blue-400/50">
        <div className="flex gap-2">
          <div className="flex-1 relative">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search address..."
              className="w-full px-3 py-2 pl-10 rounded bg-gray-800 border border-gray-600 text-white placeholder-gray-400 focus:border-blue-400 focus:outline-none"
              disabled={isSearching || isCapturing}
            />
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          </div>
          <button
            type="submit"
            disabled={isSearching || isCapturing || !searchQuery.trim()}
            className="px-4 py-2 bg-blue-600 text-white font-semibold rounded hover:bg-blue-500 disabled:bg-gray-600 disabled:text-gray-400 flex items-center gap-2"
          >
            {isSearching ? <Loader2 className="animate-spin" size={18} /> : <Search size={18} />}
          </button>
        </div>
        {searchError && (
          <p className="mt-2 text-red-400 text-sm">{searchError}</p>
        )}
      </form>

      {/* Map Container with Selection Overlay */}
      <div ref={mapContainerRef} className="flex-1 relative overflow-hidden">
        <MapContainer
          center={initialCenter}
          zoom={DEFAULT_ZOOM}
          className="w-full h-full"
          zoomControl={true}
          attributionControl={true}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            detectRetina={true}
          />
          <MapController searchResult={searchResult} mapRef={mapRef} />
        </MapContainer>

        {/* Dark overlay with 16:9 transparent window */}
        <div className="absolute inset-0 pointer-events-none">
          {/* Top overlay */}
          {selectionBox && (
            <>
              <div
                className="absolute left-0 right-0 top-0 bg-black/60"
                style={{ height: selectionBox.top }}
              />
              {/* Bottom overlay */}
              <div
                className="absolute left-0 right-0 bottom-0 bg-black/60"
                style={{ height: `calc(100% - ${selectionBox.top + selectionBox.height}px)` }}
              />
              {/* Left overlay */}
              <div
                className="absolute left-0 bg-black/60"
                style={{
                  top: selectionBox.top,
                  height: selectionBox.height,
                  width: selectionBox.left
                }}
              />
              {/* Right overlay */}
              <div
                className="absolute right-0 bg-black/60"
                style={{
                  top: selectionBox.top,
                  height: selectionBox.height,
                  width: selectionBox.left
                }}
              />
              {/* Selection border */}
              <div
                className="absolute border-4 border-yellow-400 rounded-lg"
                style={{
                  top: selectionBox.top,
                  left: selectionBox.left,
                  width: selectionBox.width,
                  height: selectionBox.height
                }}
              >
                {/* Corner indicators */}
                <div className="absolute -top-1 -left-1 w-4 h-4 border-t-4 border-l-4 border-yellow-400 rounded-tl-lg" />
                <div className="absolute -top-1 -right-1 w-4 h-4 border-t-4 border-r-4 border-yellow-400 rounded-tr-lg" />
                <div className="absolute -bottom-1 -left-1 w-4 h-4 border-b-4 border-l-4 border-yellow-400 rounded-bl-lg" />
                <div className="absolute -bottom-1 -right-1 w-4 h-4 border-b-4 border-r-4 border-yellow-400 rounded-br-lg" />

                {/* Center crosshair */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                  <div className="w-8 h-0.5 bg-yellow-400 absolute left-1/2 -translate-x-1/2" />
                  <div className="h-8 w-0.5 bg-yellow-400 absolute top-1/2 -translate-y-1/2" />
                </div>

                {/* 16:9 label */}
                <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 text-yellow-400 text-xs font-bold flex items-center gap-1">
                  <Maximize2 size={12} />
                  16:9
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Instructions & Confirm */}
      <div className="p-3 bg-black border-t-2 border-blue-400">
        <p className="text-blue-300 text-xs mb-3 text-center">
          Position the map so your location is in the center of the 16:9 frame
        </p>
        <button
          onClick={handleConfirmSelection}
          disabled={isCapturing}
          className="w-full py-3 bg-yellow-400 text-black font-bold rounded-lg hover:bg-yellow-300 disabled:bg-gray-600 disabled:text-gray-400 flex items-center justify-center gap-2 transition-colors"
        >
          {isCapturing ? (
            <>
              <Loader2 className="animate-spin" size={20} />
              Capturing Map...
            </>
          ) : (
            <>
              <Check size={20} />
              Capture This Area
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default OpenStreetMapModal;
